import {Injectable} from '@angular/core';
import firebase from 'firebase';
import {Observable} from 'rxjs/Observable';
import {ReplaySubject} from 'rxjs/ReplaySubject';
import {Subject} from 'rxjs/Subject';
import {AdvTechMobileService} from '../advtech/mobile/mobile.service';

/**
 * Abstract class for firebase services
 * @abstract
 * @class
 */
@Injectable()
export abstract class AbstractFirebaseService {
  /**
   * observable for updating the progress bar
   * @type Subject
   */
  subject: ReplaySubject<any>;

  /**
   * firebase config name
   */
  configName: string;

  /**
   * firebase config
   */
  config: {'config': any, 'configName': string, 'token': string, 'tokenExp': number};

  /**
   * @constructor
   * @param {AdvTechMobileService} advTechMobileService The mobile service
   */
  constructor(protected advTechMobileService: AdvTechMobileService) {
  }

  /**
   * Initializes the firebase service
   * @returns {Observable<any>}
   */
  init(): Observable<any> {
    this.subject = this.subject || new ReplaySubject(1);

      if (!this.config) {
        if (!this.configName) {
           return Observable.throw('you must include a firebase config name');
        }

        const config = this.advTechMobileService.getFirebaseConfig(this.configName);
        config.subscribe((res: any) => {
          this.initApp(res);
        });
      }
      else {
        this.initApp(this.config);
      }

      return this.subject;
  }

  /**
   * Handle errors
   * @param {object} error object
   * @returns {void}
   */
  protected hError(error): void {
    this.subject.error(error);
    this.done();
  }

  /**
   * @returns {void}
   */
  protected firebaseReady(): void {
    this.subject.next('ready');
  }

  /**
   * @returns {void}
   */
  protected done(): void {
    this.subject.complete();
    this.subject = null;
  }

  /**
   * @returns {Promise<string>}
   */
  private getAuthToken(): Promise<string> {
    const date = new Date(),
      tokenExp = this.config.tokenExp * 1000;

    return new Promise((resolve, reject) => {
      if (date.getTime() >= tokenExp - 10000) {
        const token = this.advTechMobileService.getFirebaseToken(this.config.configName);
        token.subscribe((res: any) => {
            this.config.token = res.token;
            this.config.tokenExp = res.tokenExp;
            resolve(this.config.token);
          },
          (error) => {
            reject(error);
          });
      }
      else {
        resolve(this.config.token);
      }
    });
  }

  /**
   * @returns {void}
   */
  private authenticate(): void {
    firebase.auth().signInWithCustomToken(this.config.token).then(
      () => this.firebaseReady(),
      () => this.hError('error occurred authenticating with firebase'),
    );
  }

  /**
   * @param {{}} config for firebase
   *
   * @returns {void}
   */
  private initApp(config: {'config': any, 'configName': string, 'token': string, 'tokenExp': number}): void {
    this.config = config;
    if (firebase.apps.length === 0) {
      firebase.initializeApp(config.config);
    }

    this.getAuthToken().then(() => this.authenticate()).catch((reason) => {
      this.hError(reason);
    });
  }

}



// WEBPACK FOOTER //
// ./src/app/shared/services/firebase/abstract-firebase.service.ts